import { states } from '@/data/statesData';
import type { Rp20UnitKey } from '@/data/rp20Data';

/**
 * Should match the RealSchools\PartnerPortal\Dto\School
 */
export interface School {
	ID: number;
	location: {
		lat: number;
		lng: number;
	};
	logo: string;
	post_title: string;
	sector: SchoolSector;
	state: (typeof states)[number]['value'];
	testimonialYoutubeUrl: string;
	tier: SchoolTier;
	type: SchoolType;
	website: string;
	yearRange: SchoolYearRange;
	partner_status: SchoolParterStatus;
}

export interface SchoolFlags {
	partnership_enabled?: boolean;
}

/**
 * Should match the RealSchools\PartnerPortal\Dto\SchoolExtended
 */
export interface AcaraRecord {
	acara_id: number;
	calendar_year: number;
	name: string;
	suburb: string;
	state: string;
	postcode: string;
	sector: string;
	school_type: string;
	campus_type: string;
	year_range: string;
	/** Null where ACARA withheld it, which is not the same as zero. */
	icsea: number | null;
	teaching_staff: number | null;
	total_enrolments: number | null;
	source: string;
}

export interface SchoolExtended extends School {
	hs_object_id: string;
	championFirstName: string;
	championLastName: string;
	/**
	 * Only populated for admin/EF users — the backend leaves it empty for everyone else.
	 * Lets staff acting for a school address a HubSpot form as its champion.
	 */
	championEmail: string;
	edit_link: string;
	efId: number;
	efFirstName: string;
	onboarding_status: SchoolsOnboardingStatus;
	pap_tier: PapTier;
	/**
	 * When the partnership started. Finance owns it in HubSpot and the portal
	 * follows, so it is read-only everywhere here. Blank for the handful of
	 * schools HubSpot has no date for, which read as year 1.
	 */
	start_date: string; // format: Y-m-d
	/** The quarter within the partnership year, 1-4, resetting each anniversary. */
	quarter: number;
	year: number;
	pl_day_date: string; // format: Y-m-d
	post_pl_debrief: string;
	leadership_onboarding_date: string;
	/** Extra delivery days bought on top of the tier. Blank is not zero. */
	extra_days_purchased: string; // format: Y-m-d
	/** HubSpot's `in_class_support`, which it labels "In-Class Support Day 1". */
	in_class_support_date: string; // format: Y-m-d
	in_class_support_date_2: string; // format: Y-m-d
	in_class_support_date_3: string; // format: Y-m-d
	in_class_support_disabled: boolean;
	parent_engagement_date: string; // format: Y-m-d
	parent_engagement_disabled: boolean;
	leader_mentoring_hours_used: number;
	/**
	 * Computed by the backend from the school's tier, unless `*_hours_override`
	 * is set. Read-only here — write the override, not this.
	 */
	leader_mentoring_hours_available: number;
	/** Blank means "whatever the tier carries", which is not the same as "0". */
	leader_mentoring_hours_override: string;
	teacher_coaching_hours_used: number;
	teacher_coaching_hours_available: number;
	teacher_coaching_hours_override: string;
	// Group school (EG BUSY)
	cluster: string;
	policy_development_and_review: string; // format: Y-m-d
	/**
	 * Sessions delivered out of four — a count, not a term number, despite what
	 * the name suggests (it is HubSpot's).
	 *
	 * **Computed by the backend from `term_dates`, not stored.** Read-only
	 * here: set the dates and the count follows. The retired HubSpot sync set
	 * it by tallying those same four dates, which left the portal holding the
	 * tally and none of the dates behind it.
	 */
	leadership_mentoring_term: number;
	principal_masterclass_term: number;
	kickstarter_session_term: number;
	/** The individual term dates, keyed by field name, each `Y-m-d` or empty. */
	term_dates: Record<string, string>;
	facilitated_rp20_units: Rp20UnitKey[];
	/**
	 * The VPA 2027 session the school has booked, if any. One field for every
	 * partnership year — it replaces a year 1 / year 2-3 pair whose option lists
	 * named the same days differently. Matches a `key` in the VPA component
	 * catalogue and `SchoolFieldOptions::Vpa2027Sessions` on the backend.
	 */
	vpa_date__2027?: string;
	/**
	 * ACARA's own record for this school, or null where it has none.
	 *
	 * **Read-only, and never the portal's figure.** It is matched to the school
	 * by ACARA id and it is annual, so `calendar_year` travels with it and
	 * nothing should render a count without showing that year. Null is the
	 * normal answer for the New Zealand partner schools, which ACARA does not
	 * list, and is not an error.
	 */
	acara?: AcaraRecord | null;
	flags: SchoolFlags;
}

export type SchoolsOnboardingStatus = (typeof schoolsOnboardingStatus)[keyof typeof schoolsOnboardingStatus];
export const schoolsOnboardingStatus = {
	Welcome: 1,
	LeadershipSurvey: 2,
	Staff: 3,
	SchoolSurvey: 4,
	ExploreResources: 6,
	Complete: 999,
	Hidden: 1000,
} as const;

export type SchoolParterStatus = (typeof schoolParterStatus)[keyof typeof schoolParterStatus];
export const schoolParterStatus = {
	Partner: 'Partner',
	PortalAccessPackage: 'Portal Access Package',
} as const;

export type SchoolType = (typeof schoolType)[keyof typeof schoolType];
export const schoolType = {
	Primary: 'Primary',
	Secondary: 'Secondary',
	Combined: 'Combined',
	Special: 'Special',
} as const;

export type SchoolSector = (typeof schoolSector)[keyof typeof schoolSector];
export const schoolSector = {
	Catholic: 'Catholic',
	Christian: 'Christian',
	Government: 'Government',
	Independent: 'Independent',
	Islamic: 'Islamic',
	Jewish: 'Jewish',
	Lutheran: 'Lutheran',
	Special: 'Special',
} as const;

export type SchoolYearRange = (typeof schoolYearRange)[keyof typeof schoolYearRange];
/** Mirrors SchoolManager::$schoolYearRanges — keys are the Hubspot internal values. */
export const schoolYearRange = {
	K_6: 'K-6',
	K_10: 'K-10',
	'K-11': 'K-11',
	K_12: 'K-12',
	Prep_6: 'Prep-6',
	Prep_9: 'Prep-9',
	Prep_10: 'Prep-10',
	Prep_12: 'Prep-12',
	R_6: 'R-6',
	R_8: 'R-8',
	R_12: 'R-12',
	T_6: 'T–6',
	Y7_9: '7-9',
	Y7_10: '7-10',
	Y7_11: '7-11',
	Y7_12: '7-12',
	'9-10': '9-10',
	Y9_12: '9-12',
	Y10_12: '10-12',
	Y11_12: '11-12',
	U: 'U',
} as const;

export type SchoolTier = (typeof schoolTier)[keyof typeof schoolTier];
export const schoolTier = {
	Customised: 'Customised',
	Immersive: 'Immersive',
	Embedded: 'Embedded',
	Guided: 'Guided',
	Supported: 'Supported',
	Platinum: 'Platinum',
	Gold: 'Gold',
	Silver: 'Silver',
	Bronze: 'Bronze',
} as const;

export type PapTier = (typeof papTier)[keyof typeof papTier];
export const papTier = {
	Base: 'Base',
	Learning: 'Learning',
} as const;
